/**
 * CR306: Compact Explainer Tooltip/Popover
 * 
 * Replaces the large collapsible explainer with a compact tooltip (desktop)
 * or tap popover (mobile) that doesn't consume layout space when closed.
 */

import { useState } from "react";
import { Info, Palette, Layers } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

export function ThemeLabsExplainerTooltip() {
  const isMobile = useIsMobile();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const content = (
    <div className="space-y-3 text-xs">
      <p className="font-semibold text-sm">Theme Labs vs Deal Cards</p>
      
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <Palette className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">Theme Labs</span>
            <Badge variant="outline" className="text-[9px] py-0 px-1 ml-1">Global</Badge>
            <p className="text-muted-foreground mt-0.5">
              Typography, colors, spacing, component defaults — applies to ALL cards
            </p>
          </div>
        </div>
        
        <div className="flex items-start gap-2">
          <Layers className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">Deal Cards</span>
            <Badge variant="outline" className="text-[9px] py-0 px-1 ml-1">Per-Type</Badge>
            <p className="text-muted-foreground mt-0.5">
              Section visibility, ordering, labels, per-type overrides
            </p>
          </div>
        </div>
      </div>
      
      <p className="text-[10px] text-muted-foreground border-t pt-2">
        <strong>Precedence:</strong> Deal Card overrides win when set, otherwise global tokens apply.
      </p>
    </div>
  );

  // Mobile: Use popover (tap to open)
  if (isMobile) {
    return (
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          >
            <Info className="h-4 w-4" />
            <span className="sr-only">Learn about Theme Labs vs Deal Cards</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="start">
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  // Desktop: Use tooltip (hover to open)
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          >
            <Info className="h-4 w-4" />
            <span className="sr-only">Learn about Theme Labs vs Deal Cards</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="w-72 p-3" side="bottom" align="start">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
